/**
 * 地区モードごとの初期値テンプレート (Sprint BG-DataSettings)。
 * 新規地区作成時 / モード切替時に dataSettings / userProfileOptions にロードする。
 *
 * 旧 v1 `old/packages/shared-types/src/modeTemplates.ts` (251 行) を完全踏襲。
 * 4 mode 全部 (bousai / ecw / walker / kids-bousai) の userFields / postFields を再現。
 *
 * mapDisplay.colorRule (旧 v1 / old2 踏襲、docs/105 A-④ で復活): 各テンプレに地図色付け
 * 規則を内包し、新規地区作成時に `getModeTemplate(mode).mapDisplay` から district.mapDisplay
 * へコピーされる (apps/admin useDistricts.createDistrict 経由)。user 側は `useColorRule` /
 * MapView / MapLegend が `District.mapDisplay.colorRule` を消費する。
 *   - ECW / WALKER: Happy(HappinessScore) × Health(HealthScore) の quadrant 4 象限
 *   - BOUSAI / KIDS_BOUSAI: category 値ベースの value buckets
 * M5 displayRules.map.colorRule (AnalysisColorRule colorAxis 形式) とは別系統 (analysisDisplayConfig 側)。
 */
import { isBuiltInMode } from '../constants/index.js';
import { POST_TYPE_FIELD_KEY, } from './dataSettings.js';
/** 文字列配列を `{value,label}[]` に変換するヘルパ (テンプレート定義時の冗長性を抑える)。 */
function opts(values) {
    return values.map((v) => ({ value: v, label: v }));
}
// ────────────────────────────────────────────
// 防災モード (BOUSAI)
// ────────────────────────────────────────────
const BOUSAI_USER_FIELDS = [
    {
        key: 'nickname',
        label: 'ニックネーム',
        type: 'text',
        mode: 'required',
        options: [],
        builtIn: true,
        description: '表示名（個人情報は入力しないでください）',
        definition: '参加者の表示名。投稿者の識別に使用するが、個人情報は含めない。',
    },
    {
        key: 'gender',
        label: '性別',
        type: 'select',
        mode: 'optional',
        options: opts(['男性', '女性', 'その他', '回答しない']),
        builtIn: true,
        description: '',
        definition: '回答者の性別。属性別クロス集計や、性別ごとの防災意識の違い分析に使用。',
    },
    {
        key: 'ageGroup',
        label: '年代',
        type: 'select',
        mode: 'optional',
        options: opts(['10代', '20代', '30代', '40代', '50代', '60代', '70代以上']),
        builtIn: true,
        description: '',
        definition: '回答者の年代。世代別の防災意識・リスク認知の傾向分析に使用。',
    },
    {
        key: 'relationship',
        label: '対象地区との関係',
        type: 'select',
        mode: 'optional',
        options: opts(['住民', '通勤・通学', '行政職員', 'ボランティア', '研究者', 'その他']),
        builtIn: true,
        description: '',
        definition: '対象地区との関係性（住民・通勤者等）。立場による視点の違いを分析する軸。',
    },
    {
        key: 'attribute',
        label: '属性',
        type: 'select',
        mode: 'optional',
        options: opts(['自治会役員', '消防団', '民生委員', 'PTA', '一般住民', '学生']),
        builtIn: true,
        description: '',
        definition: '地域での役割・所属（自治会役員・消防団等）。専門性や立場による知見の違い分析に使用。',
    },
    {
        key: 'sessions',
        label: '参加セッション',
        type: 'multiselect',
        mode: 'optional',
        options: [],
        builtIn: true,
        description: 'セッション一覧から自動生成',
        definition: '参加したセッション。時系列比較やセッション間の傾向比較に使用。',
    },
];
const BOUSAI_POST_FIELDS = [
    {
        key: POST_TYPE_FIELD_KEY,
        label: '投稿タイプ',
        type: 'select',
        mode: 'required',
        options: opts(['危険', '資源', '気づき']),
        builtIn: true,
        description: '地図上のマーカー色に反映',
        definition: '投稿を「危険」「資源」「気づき」の3タイプに分類する基本軸。地域の防災特性を俯瞰するための最も基本的な分類。',
    },
    {
        key: 'photo',
        label: '写真',
        type: 'photo',
        mode: 'optional',
        options: [],
        builtIn: true,
        description: 'カメラ撮影またはギャラリー',
        definition: '現地の状況を視覚的に記録する写真データ。AI画像解析の入力としても使用される。',
    },
    {
        key: 'title',
        label: 'タイトル',
        type: 'text',
        mode: 'required',
        options: [],
        optionsMeta: { maxLength: 100 },
        builtIn: true,
        description: '投稿の見出し',
        definition: '投稿の見出し。地図やリスト上での識別、AI解析のテキスト入力に使用。',
    },
    {
        key: 'location',
        label: '位置情報',
        type: 'location',
        mode: 'required',
        options: [],
        builtIn: true,
        description: 'GPS または地図タップ',
        definition: 'GPS座標または地図タップで取得した地理的位置。空間分析・ハザードマップ重ね合わせの基盤データ。',
    },
    // 本文は comment 1 本に統一 (V2 統一指示)。旧 MIG-3 で短文 comment / 長文 detail の 2 本に
    // 分かれていたが、保存先は post.comment 1 本で UI も同一 textarea (二重入力欄バグ) だったため
    // detail を廃止。AI prompt は source 'comment' を参照する。
    {
        key: 'comment',
        label: 'コメント',
        type: 'text',
        mode: 'optional',
        options: [],
        optionsMeta: { maxLength: 1000 },
        builtIn: true,
        description: '補足説明',
        definition: '投稿内容の補足説明テキスト。AI解析のテキスト入力としても活用される。',
    },
    // MIG-3 追加 (旧 ai-settings_bousai-v5 / bousai2 promptInputMapping `post.extraFields.userAssessment` 対応):
    // ユーザー自身の感じ方を「ホッと」(安心) / 「ハッと」(危険) の二択で表現する select。
    // 旧防災まちあるきの中核的入力で、AI 解析の入力としても活用 (assessment 推定の根拠)。
    {
        key: 'userAssessment',
        label: '自分の感じ方',
        type: 'select',
        mode: 'optional',
        options: opts(['ホッと', 'ハッと']),
        builtIn: true,
        description: '安心 (ホッと) / 危険 (ハッと) の主観評価',
        definition: '投稿者自身の主観評価。「ホッと」(安心) / 「ハッと」(危険) の二択。AI 解析の assessment 推定 + 集計の比較軸として使用。',
        displayOrder: 70,
    },
    {
        key: 'tags',
        label: 'タグ',
        type: 'multiselect',
        mode: 'optional',
        options: opts([
            '地震',
            '水害',
            '土砂',
            '火災',
            '津波',
            '夜間に危険',
            '雨天時に危険',
            '要対応:行政',
            '要対応:自治会',
            '要対応:個人',
        ]),
        builtIn: true,
        description: '複数選択可',
        definition: '災害種別（地震・水害等）や対応主体（行政・自治会等）を示す複数選択タグ。クロス集計やフィルタリングの分析軸。',
    },
];
/**
 * 防災モードの色付け規則 (投稿タイプ値ベースの value ColorRule)。
 * postFields の `postType` (危険 / 資源 / 気づき) を評価し、3 値それぞれに色を割当てる。
 * 旧 v1 では bousai は `mapDisplay: {}` だったが、docs/105 A-④ で「category 値ベースの value
 * colorRule を設定」と指定されたため、defaultColorRule 相当の value buckets を内包させる。
 */
const BOUSAI_MAP_DISPLAY = {
    colorRule: {
        kind: 'value',
        fieldKey: POST_TYPE_FIELD_KEY,
        buckets: [
            { label: '危険', expr: '==危険', color: '#EF4444', icon: '⚠️' },
            { label: '資源', expr: '==資源', color: '#10B981', icon: '✅' },
            { label: '気づき', expr: '==気づき', color: '#3B82F6', icon: '💡' },
        ],
        fallbackColor: '#9E9E9E',
        fallbackLabel: 'その他',
    },
};
export const BOUSAI_TEMPLATE = {
    dataSettings: { userFields: BOUSAI_USER_FIELDS, postFields: BOUSAI_POST_FIELDS },
    mapDisplay: BOUSAI_MAP_DISPLAY,
    userProfileOptions: {
        relationships: ['住民', '通勤・通学', '行政職員', 'ボランティア', '研究者', 'その他'],
        attributes: ['自治会役員', '消防団', '民生委員', 'PTA', '一般住民', '学生'],
    },
};
// ────────────────────────────────────────────
// ECW モード
// ────────────────────────────────────────────
const ECW_USER_FIELDS = [
    {
        key: 'nickname',
        label: 'ニックネーム',
        type: 'text',
        mode: 'required',
        options: [],
        builtIn: true,
        description: '表示名（個人情報は入力しないでください）',
        definition: '参加者の表示名。投稿者の識別に使用するが、個人情報は含めない。',
    },
    {
        key: 'ageGroup',
        label: '年代',
        type: 'select',
        mode: 'optional',
        options: opts(['10代', '20代', '30代', '40代', '50代', '60代', '70代', '80代以上', '無回答']),
        builtIn: true,
        description: '',
        definition: '回答者の年代。世代別の感性・評価傾向の分析に使用。',
    },
    {
        key: 'gender',
        label: '性別',
        type: 'select',
        mode: 'optional',
        options: opts(['男性', '女性', '無回答']),
        builtIn: true,
        description: '',
        definition: '回答者の性別。属性別クロス集計に使用。',
    },
    {
        key: 'relationship',
        label: '対象地区との関係',
        type: 'select',
        mode: 'optional',
        options: opts(['住民', '通勤・通学', '行政職員', 'ボランティア', '研究者', 'その他']),
        builtIn: true,
        description: 'user_address に相当',
        definition: '対象地区との関係性（住民・通勤者等）。立場による視点の違いを分析する軸。',
    },
    {
        key: 'sessions',
        label: '参加セッション・グループ',
        type: 'multiselect',
        mode: 'optional',
        options: [],
        builtIn: true,
        description: 'user_group に相当',
        definition: '参加したセッション・グループ。時系列比較やグループ間比較に使用。',
    },
    {
        key: 'attribute',
        label: '所属などの属性',
        type: 'text',
        mode: 'optional',
        options: [],
        builtIn: true,
        description: 'user_affiliation',
        definition: '所属組織・団体名。参加者の背景による視点の違い分析に使用。',
    },
];
const ECW_POST_FIELDS = [
    {
        key: 'photo',
        label: '写真',
        type: 'photo',
        mode: 'required',
        options: [],
        builtIn: true,
        description: 'カメラ撮影またはギャラリー',
        definition: '現地の状況を視覚的に記録する写真データ。AI画像解析の入力としても使用される。',
    },
    {
        key: 'title',
        label: 'タイトル',
        type: 'text',
        mode: 'required',
        options: [],
        optionsMeta: { maxLength: 100 },
        builtIn: true,
        description: '投稿の見出し',
        definition: '投稿の見出し。地図やリスト上での識別、AI解析のテキスト入力に使用。',
    },
    {
        key: 'location',
        label: '位置情報',
        type: 'location',
        mode: 'required',
        options: [],
        builtIn: true,
        description: 'GPS または地図タップ',
        definition: 'GPS座標または地図タップで取得した地理的位置。空間分析の基盤データ。',
    },
    {
        key: 'HappinessScore',
        label: 'Happy / Unhappy',
        type: 'rating-scale',
        mode: 'required',
        options: [],
        optionsMeta: {
            min: -3,
            max: 3,
            excludeZero: true,
            scaleLabels: { posLabel: 'Happy', negLabel: 'Unhappy' },
        },
        builtIn: true,
        description: '-3〜+3 の6段階（0なし）',
        definition: '対象に対する幸福感の主観評価（-3:Unhappy〜+3:Happy）。ウェルビーイング分析の横軸として、場所や対象物が人の幸福感に与える影響を測定。',
    },
    {
        key: 'HealthScore',
        label: 'Healthy / Unhealthy',
        type: 'rating-scale',
        mode: 'required',
        options: [],
        optionsMeta: {
            min: -3,
            max: 3,
            excludeZero: true,
            scaleLabels: { posLabel: 'Healthy', negLabel: 'Unhealthy' },
        },
        builtIn: true,
        description: '-3〜+3 の6段階（0なし）',
        definition: '対象に対する健康感の主観評価（-3:Unhealthy〜+3:Healthy）。ウェルビーイング分析の縦軸として、場所や対象物が人の健康感に与える影響を測定。',
    },
    {
        key: 'comment',
        label: 'コメント',
        type: 'text',
        mode: 'optional',
        options: [],
        optionsMeta: { maxLength: 1000 },
        builtIn: true,
        description: '補足説明',
        definition: '投稿内容の補足説明テキスト。写真だけでは伝わらない文脈をAI解析に提供する補足情報。',
    },
    {
        key: 'scope',
        label: '主体範囲',
        type: 'select',
        mode: 'optional',
        options: opts(['わたし', 'わたしも他者も']),
        builtIn: true,
        description: '',
        definition: '効果の波及範囲。「わたし」個人に限定される体験か、「わたしも他者も」に波及する環境要因かを区別する分析軸。',
    },
    {
        key: 'duration',
        label: '効果時間',
        type: 'select',
        mode: 'optional',
        options: opts(['一時的', '持続的']),
        builtIn: true,
        description: '',
        definition: '効果の時間特性。「一時的」な体験か「持続的」な環境要因かを区別する分析軸。',
    },
    // MIG-3 追加 (旧 ai-settings_ecw-v4 promptInputMapping `post.extraFields.SocialScore` 対応):
    // 「社会的つながり感」を -3..+3 (0 除外) で評価する rating-scale。
    // ECW v4 で 2 要素 (Happiness/Health) → 4 要素 (Happiness/Health/Social/LocalIdentity) に
    // 拡張するため optional として追加。v4 prompt が template var `social` として参照する。
    {
        key: 'SocialScore',
        label: '社会的つながり / 孤立',
        type: 'rating-scale',
        mode: 'optional',
        options: [],
        optionsMeta: {
            min: -3,
            max: 3,
            excludeZero: true,
            scaleLabels: { posLabel: 'つながり', negLabel: '孤立' },
        },
        builtIn: true,
        description: '-3〜+3 の6段階（0なし）',
        definition: '対象が生む社会的つながり感の主観評価（-3:孤立〜+3:つながり）。ECW v4 で導入された 4 要素ウェルビーイングの第3軸。',
        displayOrder: 80,
    },
    // MIG-3 追加 (旧 ai-settings_ecw-v4 promptInputMapping `post.extraFields.LocalIdentity` 対応):
    // 「地域らしさ・らしくなさ」を -3..+3 (0 除外) で評価する rating-scale。
    // v4 prompt が template var `local_identity` として参照する。
    {
        key: 'LocalIdentity',
        label: '地域らしさ / らしくなさ',
        type: 'rating-scale',
        mode: 'optional',
        options: [],
        optionsMeta: {
            min: -3,
            max: 3,
            excludeZero: true,
            scaleLabels: { posLabel: 'らしい', negLabel: 'らしくない' },
        },
        builtIn: true,
        description: '-3〜+3 の6段階（0なし）',
        definition: '対象がその地域らしさを表現しているかの主観評価（-3:らしくない〜+3:らしい）。ECW v4 で導入された 4 要素ウェルビーイングの第4軸。',
        displayOrder: 90,
    },
];
/**
 * ECW モードの色付け規則 (Happy × Health の quadrant 4 象限、old2 踏襲)。
 * old2 modeTemplates.ts ECW_MAP_DISPLAY (L97-111) の cells / fallback を v2 ColorRule 型に
 * 適合させて移植。xKey=HappinessScore / yKey=HealthScore (v2 postFields の実キー)、
 * `>0` / `<0` で正負 4 象限に分岐 (0 は excludeZero のため発生しない)。
 */
const ECW_MAP_DISPLAY = {
    colorRule: {
        kind: 'quadrant',
        xKey: 'HappinessScore',
        yKey: 'HealthScore',
        cells: [
            { label: 'Happy-Healthy', xExpr: '>0', yExpr: '>0', color: '#ec4899', icon: '🩷' },
            { label: 'Happy-Unhealthy', xExpr: '>0', yExpr: '<0', color: '#a855f7' },
            { label: 'Unhappy-Healthy', xExpr: '<0', yExpr: '>0', color: '#06b6d4' },
            { label: 'Unhappy-Unhealthy', xExpr: '<0', yExpr: '<0', color: '#3b82f6', icon: '💙' },
        ],
        fallbackColor: '#9E9E9E',
        fallbackLabel: 'ニュートラル',
    },
};
export const ECW_TEMPLATE = {
    dataSettings: { userFields: ECW_USER_FIELDS, postFields: ECW_POST_FIELDS },
    mapDisplay: ECW_MAP_DISPLAY,
    userProfileOptions: {
        relationships: ['住民', '通勤・通学', '行政職員', 'ボランティア', '研究者', 'その他'],
        attributes: [],
    },
};
// ────────────────────────────────────────────
// ウォーカブルモード (WALKER) — 独立テンプレート
// ────────────────────────────────────────────
const WALKER_USER_FIELDS = [
    {
        key: 'nickname',
        label: 'ニックネーム',
        type: 'text',
        mode: 'required',
        options: [],
        builtIn: true,
        description: '表示名（個人情報は入力しないでください）',
        definition: '参加者の表示名。投稿者の識別に使用するが、個人情報は含めない。',
    },
    {
        key: 'ageGroup',
        label: '年代',
        type: 'select',
        mode: 'optional',
        options: opts(['10代', '20代', '30代', '40代', '50代', '60代', '70代', '80代以上', '無回答']),
        builtIn: true,
        description: '',
        definition: '回答者の年代。世代別の歩行環境評価の傾向分析に使用。',
    },
    {
        key: 'gender',
        label: '性別',
        type: 'select',
        mode: 'optional',
        options: opts(['男性', '女性', '無回答']),
        builtIn: true,
        description: '',
        definition: '回答者の性別。属性別クロス集計に使用。',
    },
    {
        key: 'relationship',
        label: '対象地区との関係',
        type: 'select',
        mode: 'optional',
        options: opts(['住民', '通勤・通学', '行政職員', 'ボランティア', '研究者', 'その他']),
        builtIn: true,
        description: '',
        definition: '対象地区との関係性（住民・通勤者等）。立場による歩行環境評価の違いを分析する軸。',
    },
    {
        key: 'sessions',
        label: '参加セッション・グループ',
        type: 'multiselect',
        mode: 'optional',
        options: [],
        builtIn: true,
        description: '',
        definition: '参加したセッション・グループ。時系列比較やグループ間比較に使用。',
    },
    {
        key: 'attribute',
        label: '所属などの属性',
        type: 'text',
        mode: 'optional',
        options: [],
        builtIn: true,
        description: '',
        definition: '所属組織・団体名。参加者の背景による歩行環境評価の違い分析に使用。',
    },
];
const WALKER_POST_FIELDS = [
    {
        key: 'photo',
        label: '写真',
        type: 'photo',
        mode: 'required',
        options: [],
        builtIn: true,
        description: 'カメラ撮影またはギャラリー',
        definition: '現地の歩行環境を視覚的に記録する写真データ。AI画像解析の入力としても使用される。',
    },
    {
        key: 'title',
        label: 'タイトル',
        type: 'text',
        mode: 'required',
        options: [],
        optionsMeta: { maxLength: 100 },
        builtIn: true,
        description: '投稿の見出し',
        definition: '投稿の見出し。地図やリスト上での識別、AI解析のテキスト入力に使用。',
    },
    {
        key: 'location',
        label: '位置情報',
        type: 'location',
        mode: 'required',
        options: [],
        builtIn: true,
        description: 'GPS または地図タップ',
        definition: 'GPS座標または地図タップで取得した地理的位置。ウォーカビリティの空間分析に使用。',
    },
    {
        key: 'HappinessScore',
        label: 'いいね！ / もったいない',
        type: 'rating-scale',
        mode: 'required',
        options: [],
        optionsMeta: {
            min: -3,
            max: 3,
            excludeZero: true,
            scaleLabels: { posLabel: 'いいね！', negLabel: 'もったいない' },
        },
        builtIn: true,
        description: '-3〜+3 の6段階（0なし）',
        definition: '歩行環境に対する評価（-3:もったいない〜+3:いいね！）。ウォーカビリティ分析の横軸として、歩行者が感じる快適性・魅力を測定。',
    },
    {
        key: 'HealthScore',
        label: '健康的 / 不健康的',
        type: 'rating-scale',
        mode: 'required',
        options: [],
        optionsMeta: {
            min: -3,
            max: 3,
            excludeZero: true,
            scaleLabels: { posLabel: '健康的', negLabel: '不健康的' },
        },
        builtIn: true,
        description: '-3〜+3 の6段階（0なし）',
        definition: '歩行環境の健康への影響評価（-3:不健康的〜+3:健康的）。ウォーカビリティ分析の縦軸として、歩行環境が健康に与える影響を測定。',
    },
    {
        key: 'comment',
        label: 'コメント',
        type: 'text',
        mode: 'optional',
        options: [],
        optionsMeta: { maxLength: 1000 },
        builtIn: true,
        description: '補足説明',
        definition: '投稿内容の補足説明テキスト。歩行体験の文脈をAI解析に提供する補足情報。',
    },
    {
        key: 'scope',
        label: '主体範囲',
        type: 'select',
        mode: 'optional',
        options: opts(['わたし', 'わたしも他者も']),
        builtIn: true,
        description: '',
        definition: '効果の波及範囲。「わたし」個人の体験か、「わたしも他者も」に波及する環境要因かを区別する分析軸。',
    },
    {
        key: 'duration',
        label: '効果時間',
        type: 'select',
        mode: 'optional',
        options: opts(['一時的', '持続的']),
        builtIn: true,
        description: '',
        definition: '効果の時間特性。「一時的」な体験か「持続的」な環境要因かを区別する分析軸。',
    },
];
/**
 * ウォーカブルモードの色付け規則 (いいね！ × 健康的 の quadrant 4 象限、old2 踏襲)。
 * old2 modeTemplates.ts WALKER_MAP_DISPLAY (L166-180) の cells / fallback を v2 ColorRule 型に
 * 適合させて移植。xKey=HappinessScore (いいね！/もったいない) / yKey=HealthScore (健康的/不健康的)。
 */
const WALKER_MAP_DISPLAY = {
    colorRule: {
        kind: 'quadrant',
        xKey: 'HappinessScore',
        yKey: 'HealthScore',
        cells: [
            { label: 'いいね！-健康的', xExpr: '>0', yExpr: '>0', color: '#ec4899', icon: '🩷' },
            { label: 'いいね！-不健康的', xExpr: '>0', yExpr: '<0', color: '#a855f7' },
            { label: 'もったいない-健康的', xExpr: '<0', yExpr: '>0', color: '#06b6d4' },
            { label: 'もったいない-不健康的', xExpr: '<0', yExpr: '<0', color: '#3b82f6', icon: '💙' },
        ],
        fallbackColor: '#9E9E9E',
        fallbackLabel: 'ニュートラル',
    },
};
export const WALKER_TEMPLATE = {
    dataSettings: { userFields: WALKER_USER_FIELDS, postFields: WALKER_POST_FIELDS },
    mapDisplay: WALKER_MAP_DISPLAY,
    userProfileOptions: {
        relationships: ['住民', '通勤・通学', '行政職員', 'ボランティア', '研究者', 'その他'],
        attributes: [],
    },
};
// ────────────────────────────────────────────
// 子供向け防災モード (KIDS_BOUSAI)
// ────────────────────────────────────────────
const KIDS_BOUSAI_USER_FIELDS = [
    {
        key: 'nickname',
        label: 'ニックネーム',
        type: 'text',
        mode: 'required',
        options: [],
        builtIn: true,
        description: 'じぶんの表示めい（ほんとうの名前はかかない）',
        definition: 'じぶんの名前のかわりにつかう「よびな」。ほんとうの名前はかかないでね。',
    },
    {
        key: 'ageGroup',
        label: '学年',
        type: 'select',
        mode: 'optional',
        options: opts(['未就学', '小学1〜2年', '小学3〜4年', '小学5〜6年', '中学生', 'その他']),
        builtIn: true,
        description: '',
        definition: '学年。年れいごとにどんなちがいがあるか見るために使います。',
    },
    {
        key: 'sessions',
        label: '参加セッション',
        type: 'multiselect',
        mode: 'optional',
        options: [],
        builtIn: true,
        description: 'セッション一覧から自動生成',
        definition: '参加セッション。いつのまち歩きかを記録します。',
    },
];
const KIDS_BOUSAI_POST_FIELDS = [
    {
        key: POST_TYPE_FIELD_KEY,
        label: '投稿タイプ',
        type: 'select',
        mode: 'required',
        options: opts(['ホッと（安心）', 'ハッと（きけん）', 'ヒント（気づき）']),
        builtIn: true,
        description: 'ホッと／ハッと／ヒントのどれか',
        definition: '「ホッと（安心）」「ハッと（きけん）」「ヒント（気づき）」の3つのうちどれかを選ぶ分類。まちの安全について考えるための基本分類。',
    },
    {
        key: 'photo',
        label: 'しゃしん',
        type: 'photo',
        mode: 'optional',
        options: [],
        builtIn: true,
        description: 'カメラでとる or アルバムからえらぶ',
        definition: 'しゃしん。見つけたものをカメラでとって記録します。',
    },
    {
        key: 'title',
        label: 'タイトル',
        type: 'text',
        mode: 'required',
        options: [],
        optionsMeta: { maxLength: 100 },
        builtIn: true,
        description: '見出し（たんごでもOK）',
        definition: '見出し。見つけたものを短いことばで表します。',
    },
    {
        key: 'location',
        label: '場所',
        type: 'location',
        mode: 'required',
        options: [],
        builtIn: true,
        description: 'GPS または 地図をタップ',
        definition: '場所。見つけた場所をGPSや地図タップで記録します。',
    },
    {
        key: 'comment',
        label: 'コメント',
        type: 'text',
        mode: 'optional',
        options: [],
        optionsMeta: { maxLength: 1000 },
        builtIn: true,
        description: 'きづいたこと・ひとことメモ',
        definition: 'コメント。気づいたことを自由に書きます。',
    },
];
/**
 * 子供向け防災モードの色付け規則 (投稿タイプ値ベースの value ColorRule)。
 * postFields の `postType` (ホッと（安心） / ハッと（きけん） / ヒント（気づき）) を評価。
 * BOUSAI と同系統だが options 値が子供向け表記のため、expr もその実値に合わせる。
 */
const KIDS_BOUSAI_MAP_DISPLAY = {
    colorRule: {
        kind: 'value',
        fieldKey: POST_TYPE_FIELD_KEY,
        buckets: [
            { label: 'ホッと（安心）', expr: '==ホッと（安心）', color: '#10B981', icon: '💚' },
            { label: 'ハッと（きけん）', expr: '==ハッと（きけん）', color: '#EF4444', icon: '⚠️' },
            { label: 'ヒント（気づき）', expr: '==ヒント（気づき）', color: '#3B82F6', icon: '💡' },
        ],
        fallbackColor: '#9E9E9E',
        fallbackLabel: 'その他',
    },
};
export const KIDS_BOUSAI_TEMPLATE = {
    dataSettings: { userFields: KIDS_BOUSAI_USER_FIELDS, postFields: KIDS_BOUSAI_POST_FIELDS },
    mapDisplay: KIDS_BOUSAI_MAP_DISPLAY,
    userProfileOptions: {
        relationships: ['じぶんの家の近く', '学校の近く', '通学路', 'ときどきいく場所', 'その他'],
        attributes: [],
    },
};
// ────────────────────────────────────────────
// テンプレート取得
// ────────────────────────────────────────────
/**
 * @deprecated 【廃止予定 / legacy fallback】静的モードテンプレート取得 (モードドリブン化リファクタ 2026-07-24)。
 *
 * 地区の初期 dataSettings / mapDisplay の唯一の正は Firestore `modeProfiles` (M10) —
 * 組込 4 モード分も seed-mode-profiles.mjs で ModeProfile doc として登録済みで、
 * 新規地区は ModeProfile 選択必須のため本関数には到達しない。
 * 残存理由: ModeProfile 登場以前に作られた legacy 地区 (profileId 無し) の user 側
 * postFields fallback (postFieldsHelpers / SettingsModal) のみ。
 *
 * 注意: 未知の custom mode を渡すと **BOUSAI_TEMPLATE に落ちる** (default 分岐)。
 * custom モード地区には決して使わないこと (District に転写済みの dataSettings を読む)。
 * 撤去条件: legacy 地区の dataSettings 転写完了 or 全アーカイブ時。
 */
export function getModeTemplate(mode) {
    switch (mode) {
        case 'ecw':
            return ECW_TEMPLATE;
        case 'walker':
            return WALKER_TEMPLATE;
        case 'kids-bousai':
            return KIDS_BOUSAI_TEMPLATE;
        case 'bousai':
        default:
            return BOUSAI_TEMPLATE;
    }
}
/**
 * @deprecated 【廃止予定 / legacy fallback】組込モードの固定ラベル (モードドリブン化リファクタ 2026-07-24)。
 * 表示名の正は ModeProfile.modeLabel (→ District.modeLabel に転写)。本 Record は
 * ModeProfile 未登録の legacy データ表示用 fallback (getModeLabel 内) にのみ残存。
 */
export const MODE_LABELS = {
    bousai: '防災モード',
    ecw: 'ECW（街歩き）モード',
    walker: 'ウォーカブルモード',
    'kids-bousai': '子供向け防災モード',
};
/**
 * モードの表示ラベルを解決する (B2、issue #32 / モードドリブン化リファクタ 2026-07-24)。
 *
 * 解決順 (ModeProfile を登記簿とする新方式):
 *  1. `customLabel` (ModeProfile.modeLabel / District.modeLabel 転写値) があれば **組込モードでも** 最優先
 *  2. 組込モード: `MODE_LABELS` の固定ラベル (@deprecated fallback — ModeProfile 未登録の legacy 表示用)
 *  3. それ以外: key 文字列そのもの
 *
 * 「内部 key をユーザー UI に出さない」全体ルールのため、custom モードを user 画面に出す箇所は
 * 必ず customLabel (地区が保持する日本語ラベル) を渡すこと。
 */
export function getModeLabel(mode, customLabel) {
    const trimmed = customLabel?.trim();
    if (trimmed)
        return trimmed;
    if (isBuiltInMode(mode))
        return MODE_LABELS[mode];
    return mode;
}
//# sourceMappingURL=modeTemplates.js.map